import { onBeforeUnmount, onMounted, type Ref } from 'vue';

export interface DialogFocusOptions {
  /** Escape pressed anywhere on the page while this dialog is on top; the dialog decides whether it closes. */
  onEscape?: () => void;
  /** Where focus goes on close when the element that opened the dialog is no longer on the page. */
  fallbackFocus?: () => HTMLElement | null | undefined;
}

const TABBABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(', ');

const HIDDEN_VISIBILITIES: readonly string[] = ['hidden', 'collapse'];

// The dialogs open now, the last one on top: only that one answers Escape and keeps Tab inside.
const openDialogs: symbol[] = [];

// Hidden by CSS too: `display: none` on the element or a parent up to the panel, or `visibility` hidden or collapsed.
const isRendered = (element: HTMLElement, panel: HTMLElement): boolean => {
  if (HIDDEN_VISIBILITIES.includes(getComputedStyle(element).visibility)) return false;
  for (let node: HTMLElement | null = element; node && node !== panel; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return false;
  }
  return true;
};

const tabbablesIn = (panel: HTMLElement): HTMLElement[] =>
  Array.from(panel.querySelectorAll<HTMLElement>(TABBABLE)).filter((element) => element.closest('[hidden], [inert]') === null && isRendered(element, panel));

const keepTabInside = (event: KeyboardEvent, panel: HTMLElement) => {
  const tabbables = tabbablesIn(panel);
  const first = tabbables[0];
  const last = tabbables[tabbables.length - 1];
  if (!first || !last) {
    event.preventDefault();
    panel.focus();
    return;
  }
  const active = document.activeElement;
  const outside = !(active instanceof Node) || !panel.contains(active);
  if (event.shiftKey && (outside || active === first || active === panel)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (outside || active === last)) {
    event.preventDefault();
    first.focus();
  }
};

/** The element holding focus, or null while it is on the page body. */
export const focusedElement = (): HTMLElement | null => {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
};

// Where focus lands when the element holding it leaves the page and nothing else is named: the dialog or page around it.
const FALLBACK_CONTAINERS = '[role="dialog"], [aria-modal="true"], main';

type FocusCandidate = () => HTMLElement | null | undefined;

// Long enough for a confirmed action's requests (the API client gives up on one after 30 seconds), short enough that a
// confirm whose action keeps its opener stops watching every change to the page.
export const REFOCUS_WATCH_MS = 60_000;

/** Focuses `target` if it is on the page, and tells whether it took focus: an element without a tabindex may not. */
const takesFocus = (target: HTMLElement | null | undefined): boolean => {
  if (!target?.isConnected) return false;
  target.focus({ preventScroll: true });
  return document.activeElement === target;
};

/**
 * Keeps focus on the page when `element`, holding it, is taken off the page, as when a confirmed action removes the
 * button that asked for it: focus goes to `fallback()`, else to the dialog or main landmark that held `element`, else to
 * the first element that dialog can tab to, else to the main landmark. It stops watching once `element` is removed, once
 * focus moves to anything else, or after REFOCUS_WATCH_MS.
 */
export function refocusIfRemoved(element: HTMLElement, fallback?: FocusCandidate): void {
  const container = element.parentElement?.closest<HTMLElement>(FALLBACK_CONTAINERS) ?? null;
  const candidates: FocusCandidate[] = [
    () => fallback?.(),
    () => container,
    () => (container?.isConnected ? tabbablesIn(container)[0] : null),
    () => document.querySelector<HTMLElement>('main'),
  ];
  const onFocusIn = (event: FocusEvent) => {
    if (event.target !== element) stop();
  };
  const observer = new MutationObserver(() => {
    if (element.isConnected) return;
    stop();
    if (focusedElement() !== null) return;
    candidates.some((candidate) => takesFocus(candidate()));
  });
  const watchEnd = setTimeout(stop, REFOCUS_WATCH_MS);
  function stop() {
    clearTimeout(watchEnd);
    observer.disconnect();
    document.removeEventListener('focusin', onFocusIn, true);
  }
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('focusin', onFocusIn, true);
}

/**
 * For a dialog shown by mounting it: focus moves to `target` when it mounts and goes back to the element that had it
 * when the dialog unmounts. While it is the topmost dialog, Escape is heard on the whole document, so it still works
 * once focus has fallen to the page body (a focused button that gets disabled drops it there), and Tab wraps around
 * inside a `target` marked `aria-modal="true"`.
 */
export function useDialogFocus(target: Readonly<Ref<HTMLElement | null>>, options: DialogFocusOptions = {}): void {
  const id = Symbol('dialog');
  let opener: HTMLElement | null = null;

  const onKeydown = (event: KeyboardEvent) => {
    if (openDialogs[openDialogs.length - 1] !== id || event.defaultPrevented) return;
    if (event.key === 'Escape') {
      options.onEscape?.();
      return;
    }
    const panel = target.value;
    if (event.key === 'Tab' && panel?.getAttribute('aria-modal') === 'true') keepTabInside(event, panel);
  };

  onMounted(() => {
    opener = focusedElement();
    openDialogs.push(id);
    document.addEventListener('keydown', onKeydown);
    target.value?.focus();
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
    const index = openDialogs.indexOf(id);
    if (index !== -1) openDialogs.splice(index, 1);
    (opener?.isConnected ? opener : options.fallbackFocus?.())?.focus();
    opener = null;
  });
}

/**
 * For a panel shown by mounting it that is not a dialog, such as a banner: it takes focus when it mounts, unless a
 * dialog is open, and gives focus back when it unmounts holding it. It stays out of the dialogs above, so an open dialog
 * keeps its Escape and its Tab trap.
 */
export function usePanelFocus(target: Readonly<Ref<HTMLElement | null>>, options: Pick<DialogFocusOptions, 'fallbackFocus'> = {}): void {
  let opener: HTMLElement | null = null;

  onMounted(() => {
    if (openDialogs.length > 0) return;
    opener = focusedElement();
    target.value?.focus();
  });

  onBeforeUnmount(() => {
    const active = focusedElement();
    const holdsFocus = active === null || (target.value?.contains(active) ?? false);
    if (holdsFocus) (opener?.isConnected ? opener : options.fallbackFocus?.())?.focus();
    opener = null;
  });
}
